<template>
<div style="margin: 25px 0;"
    v-if="question.config.type=='Ranking' || question.config.type=='SelectTable'">
        <template v-if="question.config.type=='Ranking'">
            <h3 align='center' style="text-transform:uppercase;">{{question.config.type}} settings</h3>
            <v-text-field type="number" v-model="settings[question.config.type].min" label="Minimum input items ranked" outlined/>
            <v-text-field type="number" v-model="settings[question.config.type].max" label="Maximum input items ranked" outlined/>
        </template>

    <template v-if="question.config.type=='SelectTable'">

        <v-expansion-panels>
            <v-expansion-panel>
                <v-expansion-panel-header expand-icon="fas fa-angle-right" style="background-color: lightgrey;">
                    <span style="font-size; 1.2rem; color: black">
                        SelectTable Inputs (Columns)
                    </span>
                </v-expansion-panel-header>

                <v-expansion-panel-content :class="$style.selectTableExpansionSection">
                    <!-- Quick Apply Section -->
                    <fieldset style="border: 1px solid #C1C1C1; border-radius: 5px;">
                        <legend style="padding: 0 15px;">
                            Quick Apply
                            <v-tooltip right color="black">
                                <template v-slot:activator="{on, attrs}">
                                    <v-icon class='ml-2'
                                        color="black"
                                        small 
                                        v-bind="attrs" 
                                        v-on="on">fas fa-info-circle</v-icon>
                                </template>
                                <span>
                                    Quick apply allows you to enter multiple inputs at once, for one language at a time. Only one input may be entered per line. 
                                    <br> 
                                    Do this once for each language.
                                </span>
                            </v-tooltip>
                        </legend>
                        <div :class="$style.selectTableQuickApplySection">
                            <div :class="$style.selectTableQuickApplyChild">
                                <div>
                                    <p :class="$style.selectTableQuickApplyLabel">
                                        Language Type 
                                    </p>
                                    <v-select 
                                        :items="language_list" 
                                        v-model="quick_apply_language"
                                        outlined
                                        dense />
                                </div>
                                <div :class="$style.applyBtn">
                                    <v-btn 
                                        style="text-transform: initial;"
                                        depressed
                                        small
                                        @click="selectTable.apply_inputs()" 
                                        color="primary" 
                                        v-if="quick_apply_language">Apply</v-btn> 
                                </div>
                            </div>
                            <div :class="$style.selectTableQuickApplyChild">
                                <p :class="$style.groupQuickApplyLabel" style="margin-bottom: 0px;">SelectTable Inputs (Columns)</p>
                                <span style="font-size: 14px; color: grey;"><sup>*</sup>One input per line</span>
                                <TextEditor
                                    :disabled="!quick_apply_language"
                                    v-model="quick_apply"
                                    :editor-init="editorInit"
                                    @updateText="quick_apply = $event"
                                /> 
                            </div>
                        </div>
                    </fieldset>

                    <div style="margin: 15px 0 0; display: flex; flex-direction: row; align-items: center; justify-content: space-between;">
                        <v-btn color="primary" @click='selectTable.add_item()' depressed small>Add a column</v-btn>

                        <div style="margin-left: auto;">
                            <v-menu offset-y nudge-left='50'>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        class="mr-5"
                                        large
                                        icon
                                        color="primary"
                                        dark
                                        v-bind="attrs"
                                        v-on="on"
                                        :disabled="checkColumnTrashDisabled(settings[question.config.type].trash)"
                                        >
                                        <div :class="$style.columnTrashSection">
                                            <v-badge right>
                                                <template v-slot:badge>
                                                    <span>{{checkColumnTrashDisabled(settings[question.config.type].trash) ? 0 : settings[question.config.type].trash.length}}</span>
                                                </template>
                                                <svgIcon :src="trashIcon" />
                                            </v-badge>
                                            <span :class="$style.columnTrashLabel">
                                                Column Trash
                                            </span>
                                        </div>
                                    </v-btn>
                                </template>
                                <v-list v-if="!checkColumnTrashDisabled(settings[question.config.type].trash)">
                                    <draggable class="list-group" :list="settings[question.config.type].trash" :group="'select_table_options'">
                                        <v-list-item v-for="(item, index) in settings[question.config.type].trash" :key="'coloumn_item_'+index" :move="selectTable.check_allowUndelete">
                                            <div :class="$style.item">
                                                ID: {{item.id}}
                                                <hr>
                                                Allow Undelete: {{selectTable.allowUndelete(item)}}
                                            </div>
                                        </v-list-item>
                                    </draggable>
                                </v-list>
                            </v-menu>
                        </div>
                    </div>

                    <template v-if="state=='ready'">
                        <div style="width: 100%;">
                            <draggable 
                                :class="[{[$style.emptyColumnWrapper]: settings[question.config.type].items.length == 0}, 'list-group']"
                                :list="settings[question.config.type].items" 
                                :group="'select_table_options'">
                                <template v-for="(item,index) in settings[question.config.type].items">     
                                    <div :class='$style.item' :key="'group_item_' + index">
                                        <v-icon 
                                            @click="selectTable.remove_item(index);"
                                            color="black" 
                                            small 
                                            :class="$style.deleteIcon">
                                            fa fa-times-circle
                                        </v-icon>
                                        <!-- Column Position Drag Section -->
                                        <aside :class="[$style.columnItemDragSection, 'handle']">
                                            <svgIcon :src="dragIcon" />
                                        </aside>

                                        <!-- Column Main Section -->
                                        <main :class="$style.columnItemMainSection">
                                            <v-expansion-panels>
                                                <v-expansion-panel class="custom-icon-rotation">
                                                    <!-- Column Header -->
                                                    <v-expansion-panel-header expand-icon="fas fa-angle-right">
                                                        <span>
                                                            Drag to reorder - SelectTabe Input (Column) #{{index + 1}}
                                                        </span>
                                                    </v-expansion-panel-header>

                                                    <!-- Column Body -->
                                                    <v-expansion-panel-content :class="$style.columnExpansionContentSection">
                                                        <edit_labels 
                                                            @update="selectTable.update_labels" 
                                                            :id="'question_'+question.config.id+'_group_setting_'+item.id" 
                                                            :content="item.label" 
                                                            :index="index" 
                                                            type="select table option" 
                                                            :languages="languages"/>                 
                                                    </v-expansion-panel-content>
                                                </v-expansion-panel>
                                            </v-expansion-panels>
                                        </main>

                                    </div>
                                </template>
                            </draggable>
                        </div>
                    </template>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>    
    </template>
</div>
</template>

<script>
const selectTable = function(component){
    var self = this;

    this.add_item = function(){
        var item = {
            id: this.new_id(),
            value: this.new_id(),
            label: {},
            enabled: true
        }

        component.settings[component.question.config.type].items.push(item);
    }

    this.new_id = function(){
        var ids = {};
        for(let i=0; i<component.settings[component.question.config.type].items.length; i++){
            var item = component.settings[component.question.config.type].items[i];
            ids[item.id] = true;
        }

        var id = 1;
        for(let i=0; i<component.settings[component.question.config.type].items.length; i++){
            var item = component.settings[component.question.config.type].items[i];
            if(!ids[id]){
                break;
            }
            id++;
        }


        return id;
    }

    this.allowUndelete = function(item){
        var itemMap = {};
        for(let i=0; i<component.settings[component.question.config.type].items.length; i++){
            var listItem = component.settings[component.question.config.type].items[i];
            itemMap[listItem.id] = listItem;
        }
        var allow = typeof itemMap[item.id] == 'undefined';
        
        return allow;
    }

    this.check_allowUndelete = function(event){
        return self.allowUndelete(event.draggedContext.element);
    }

    this.apply_inputs = function(){
        var data = component.quick_apply;
        var inputs = JSON.stringify(component.settings[component.question.config.type].items);
        inputs = JSON.parse(inputs);

        function list_data(data){
            var list = data.split(/\r?\n/);
            if(list.length==1){
                list = data.split(/\t/);
            }
            var output = [];
            for(let i=0; i<list.length; i++){
                var item = list[i];
                if(item.length>0){
                    output.push(item);
                }
            }
            return output;
        }

        var list = list_data(data);
        var offset = (list.length - inputs.length);

        component.state='apply_inputs';

        //Setup input targets
        if(offset>0){
            for(let i=0; i<offset; i++){
                self.add_item();
            }
        }

        for(let i=0; i<list.length; i++){
            var list_item = list[i];
            var target = component.settings[component.question.config.type].items[i];
            target.label[component.quick_apply_language] = list_item;
        }


        setTimeout(function(){
            component.quick_apply_language = null;
            component.quick_apply = null;
            component.state='ready';
        },800)
    }

    this.update_labels = function(data){
        component.settings[component.question.config.type].items[data.index].label = data.values;
    }

    this.remove_item = function(index) {
        if(component.settings[component.question.config.type].trash === undefined) {
            component.settings[component.question.config.type].trash = [];
        }
        component.settings[component.question.config.type].trash.push(component.settings[component.question.config.type].items[index]);
        component.settings[component.question.config.type].items.splice(index, 1);
    }

}
import draggable from "vuedraggable";
import edit_labels from "./edit_labels.vue";

import TextEditor from '@/components/TextEditor/TextEditor.vue';

import svgIcon from '@/components/svg/svg_icon.vue';
import dragIcon from '@/assets/images/icons/drag.svg';
import trashIcon from '@/assets/images/icons/trash.svg';

export default {
    name: 'settings_editor',
    props: {
        group_index: {
            type: Number,
            required: true
        },
        question: {
            type: Object,
            required: true
        },
        languages: {
            type: Array,
            required: true
        }
    },
    components: {
        draggable,
        edit_labels,
        TextEditor,
        svgIcon
    },
    created: function(){
        var settings = JSON.stringify(this.question.config.groups[this.group_index].type_config);
        this.settings = JSON.parse(settings);

        var component = this;
        component.selectTable = new selectTable(component);

    },
    data: function(){
        return {
            state: 'ready',
            settings: null,
            selectTable: null,
            quick_apply: null,
            quick_apply_language: null,
            editorInit : {
                height: 300,
                menubar: false,
                plugins: ['lists', 'paste'],
                toolbar:
                'formatselect | bold italic underline| \
                alignleft aligncenter alignright alignjustify | \
                bullist numlist outdent indent | superscript subscript | paste',
                paste_as_text: true,
                paste_merge_formats: false,
                paste_preprocess: function(plugin, args) {
                    let pattern = "<br />";
                    args.content = args.content.replaceAll(pattern, "</p><p>\n");
                }
            }
        }
    },
    methods: {
        checkColumnTrashDisabled: function(trash) {
            if(trash === undefined) {
                return true;
            }
            if(trash.length <= 0) {
                return true;
            }
            return false;
        }
    },
    computed: {
        language_list: function(){
            var list = [];
            for(let i=0; i<this.languages.length; i++){
                var language = this.languages[i];
                list.push({
                    text: language.name,
                    value: language.code
                })
            }

            return list;
        },
        dragIcon: function() {
            return dragIcon;
        },
        trashIcon: function() {
            return trashIcon;
        }
    },
    watch: {
        settings: {
            deep: true,
            handler: function(){
                this.$emit('update',{
                    index: this.group_index,
                    settings: this.settings
                })
            }
        }
    }
}
</script>

<style module>
.list_container{
    float: left;
}

.item{
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    position: relative;
    margin: 15px 0px 15px 0px;
    background-color: inherit;
}

.selectTableExpansionSection {
    background-color: lightgrey;
    border-top: 1px solid #E5E5E5;
    padding-top: 15px;
}

.selectTableQuickApplySection {
    display:flex; 
    flex-direction: row; 
    flex-wrap: wrap-reverse; 
    align-items: center; 
    justify-content: space-around;
}

.selectTableQuickApplyLabel {
    margin-bottom: 0px; 
    color: black; 
    font-size: 14px;
}

.selectTableQuickApplyChild {
    width: 45%;
}

.applyBtn {
    text-align: right;
}

.columnTrashSection {
    display: flex; 
    flex-direction: column; 
    align-items: center; 
    cursor: pointer;
}

.columnTrashLabel {
    font-size: 10px; 
    color: black; 
    font-weight: 400; 
    text-transform: initial
}

.emptyColumnWrapper {
    border: 1px dashed silver;
    height: 50px;
    margin-top: 15px;
}

.emptyColumnWrapper:after {
    content: 'Drag Columns to here...';
    margin: 0 auto;
    vertical-align: -50%;
    text-align: center;
    padding: 0 15px;
}

.columnItemDragSection {
    cursor: grab; 
    padding: 15px 5px 0 5px; 
    display: flex; 
    flex-direction: column; 
    text-align: center; 
    width: 4.5%; 
    height: 65px; 
    background-color: white;
    cursor: grab;
    justify-content: center;
}

.columnItemDragSection:active {
    cursor: grabbing;
}

.columnExpansionContentSection {
    background-color: white; 
    border-top: 1px solid #E5E5E5; 
    padding-top: 15px;
}

.columnItemMainSection {
    width: 95.5%;
}

.deleteIcon {
    position: absolute; 
    top: -9px; 
    right: -5px; 
    z-index:2;
    cursor: pointer;
}
</style>